import { forwardRef } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";

export type CardTone = "light" | "ink" | "volt" | "ember";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: CardTone;
  /** Adds hover lift + shadow for clickable/feature cards. */
  interactive?: boolean;
  padded?: boolean;
}

const tones: Record<CardTone, string> = {
  // Big-rounded, airy white card with a hairline + soft shadow.
  light: "bg-surface text-body ring-1 ring-line shadow-sm",
  // Deep ink surface for high-contrast feature panels.
  ink: "bg-ink-800 text-ondark ring-1 ring-ink-line shadow-md",
  // Coral→orange gradient with a coral glow (the loud feature card).
  volt: "bg-grad-coral text-white shadow-volt",
  // Violet gradient with a violet glow.
  ember: "bg-grad-violet text-white shadow-ember",
};

/** A surface container. The base building block for grouped content. */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    { tone = "light", interactive = false, padded = true, className, children, ...rest },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        "rounded-xl",
        padded && "p-6",
        tones[tone],
        interactive &&
          "transition-all duration-200 ease-kinetic hover:-translate-y-1 hover:shadow-lg cursor-pointer",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  ),
);
Card.displayName = "Card";

export function CardHeader({
  title,
  subtitle,
  action,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mb-4 flex items-start justify-between gap-4", className)}>
      <div>
        <h3 className="font-display text-lg font-bold leading-snug">{title}</h3>
        {subtitle && (
          <p className="mt-1 text-sm text-body-muted">{subtitle}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
